import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { systemService } from '@/api/services/systemService'

// ── Cities ───────────────────────────────────────────────────────────────
export function useCities(p?: Parameters<typeof systemService.cities.list>[0]) {
  return useQuery({ queryKey: ['cities', p], queryFn: () => systemService.cities.list(p), staleTime: 300_000 })
}
export function useCreateUpdateCity() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (d: Parameters<typeof systemService.cities.createUpdate>[0]) => systemService.cities.createUpdate(d), onSuccess: () => qc.invalidateQueries({ queryKey: ['cities'] }) })
}
export function useDeleteCity() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: number) => systemService.cities.delete(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['cities'] }) })
}

// ── States ───────────────────────────────────────────────────────────────
export function useStates(p?: Parameters<typeof systemService.states.list>[0]) {
  return useQuery({ queryKey: ['states', p], queryFn: () => systemService.states.list(p), staleTime: 300_000 })
}
export function useCreateUpdateState() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (d: Parameters<typeof systemService.states.createUpdate>[0]) => systemService.states.createUpdate(d), onSuccess: () => qc.invalidateQueries({ queryKey: ['states'] }) })
}
export function useDeleteState() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: number) => systemService.states.delete(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['states'] }) })
}

// ── Dialects ─────────────────────────────────────────────────────────────
export function useDialects(p?: Parameters<typeof systemService.dialects.list>[0]) {
  return useQuery({ queryKey: ['dialects', p], queryFn: () => systemService.dialects.list(p), staleTime: 300_000 })
}
export function useCreateUpdateDialect() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (d: Parameters<typeof systemService.dialects.createUpdate>[0]) => systemService.dialects.createUpdate(d), onSuccess: () => qc.invalidateQueries({ queryKey: ['dialects'] }) })
}
export function useDeleteDialect() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: number) => systemService.dialects.delete(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['dialects'] }) })
}

// ── Languages ────────────────────────────────────────────────────────────
export function useLanguages(p?: Parameters<typeof systemService.languages.list>[0]) {
  return useQuery({ queryKey: ['languages', p], queryFn: () => systemService.languages.list(p), staleTime: 300_000 })
}
export function useUploadFlagImage() {
  return useMutation({ mutationFn: (file: File) => systemService.languages.uploadFlag(file) })
}
export function useCreateLanguage() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (d: Parameters<typeof systemService.languages.create>[0]) => systemService.languages.create(d), onSuccess: () => qc.invalidateQueries({ queryKey: ['languages'] }) })
}
export function useUpdateLanguage() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: ({ id, ...d }: { id: number } & Parameters<typeof systemService.languages.update>[1]) => systemService.languages.update(id, d), onSuccess: () => qc.invalidateQueries({ queryKey: ['languages'] }) })
}
export function useDeleteLanguage() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: number) => systemService.languages.delete(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['languages'] }) })
}

// ── Pickup exclusions ────────────────────────────────────────────────────
export function usePickups(p?: Parameters<typeof systemService.pickups.list>[0]) {
  return useQuery({ queryKey: ['pickups', p], queryFn: () => systemService.pickups.list(p), staleTime: 300_000 })
}
export function useCreateUpdatePickup() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (d: Parameters<typeof systemService.pickups.createUpdate>[0]) => systemService.pickups.createUpdate(d), onSuccess: () => qc.invalidateQueries({ queryKey: ['pickups'] }) })
}
export function useDeletePickup() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: number) => systemService.pickups.delete(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['pickups'] }) })
}

// ── App settings ─────────────────────────────────────────────────────────
export function useAppSettings(p?: Parameters<typeof systemService.appSettings.get>[0]) {
  return useQuery({ queryKey: ['app-settings', p], queryFn: () => systemService.appSettings.get(p), staleTime: 300_000 })
}
export function useUpdateAppSettings() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (d: { id: number; key: string; value: string }) => systemService.appSettings.update(d), onSuccess: () => qc.invalidateQueries({ queryKey: ['app-settings'] }) })
}

// ── Terms & conditions ───────────────────────────────────────────────────
export function useTermsConditions(locale: string) {
  return useQuery({ queryKey: ['terms-conditions', locale], queryFn: () => systemService.termsConditions.get(locale), staleTime: 300_000 })
}
export function useUpdateTermsConditions() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (d: Parameters<typeof systemService.termsConditions.update>[0]) => systemService.termsConditions.update(d), onSuccess: () => qc.invalidateQueries({ queryKey: ['terms-conditions'] }) })
}

// ── Dynamic Services (settings) ─────────────────────────────────────────
export function useDynamicServices(p?: Parameters<typeof systemService.dynamicServices.list>[0]) {
  return useQuery({ queryKey: ['dynamic-services', p], queryFn: () => systemService.dynamicServices.list(p), staleTime: 60_000 })
}
export function useCreateUpdateDynamicService() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (d: Parameters<typeof systemService.dynamicServices.createUpdate>[0]) => systemService.dynamicServices.createUpdate(d), onSuccess: () => qc.invalidateQueries({ queryKey: ['dynamic-services'] }) })
}
export function useDeleteDynamicService() {
  const qc = useQueryClient()
  return useMutation({ mutationFn: (id: number) => systemService.dynamicServices.delete(id), onSuccess: () => qc.invalidateQueries({ queryKey: ['dynamic-services'] }) })
}
